import useGetProjectEstimate from '@/api/queries/project/useGetProjectEstimate';
import { Accordion } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { FC } from 'react';

type EstimateFinancialsProps = {
  projectId: string;
  estimateId: string;
};

const EstimateFinancials: FC<EstimateFinancialsProps> = (props) => {
  const { projectId, estimateId } = props;
  const { estimate } = useGetProjectEstimate(projectId, estimateId);

  if (!estimate) {
    return null;
  }

  const grants = estimate.grants ?? [];

  return (
    <div className="px-3.5 py-5 border-b typography:prose">
      <Accordion
        title="Financials"
        chevronPosition="left"
        defaultOpen
        summary={
          <span className="flex gap-2">
            <span>{grants.length > 0 ? 'Total payable' : 'Total'}</span>
            <span className="h4">
              <CurrencyValue {...estimate.totalPayable} />
            </span>
          </span>
        }
      >
        <dl className="mt-2 grid grid-cols-2 items-end gap-y-1.5 m-0 [&>*]:m-0">
          <dt id="estimate-subtotal">
            <p className="supporting-body text-gray-500">Subtotal</p>
          </dt>
          <dd aria-labelledby="estimate-subtotal">
            <p className="text-right">
              <CurrencyValue {...estimate.subtotalExTax} />
            </p>
          </dd>

          <dt id="estimate-tax">
            <p className="supporting-body text-gray-500 pb-1.5">Tax</p>
          </dt>
          <dd aria-labelledby="estimate-tax">
            <p className="text-right pb-1.5">
              <CurrencyValue {...estimate.totalTax} />
            </p>
          </dd>

          {grants.map((grant) => (
            <GrantDeductionRow key={grant.id} grant={grant} />
          ))}

          <dt id="estimate-total">
            <h4 className="border-t pt-3.5">
              {grants.length > 0 ? 'Total payable' : 'Total'}
            </h4>
          </dt>
          <dd aria-labelledby="estimate-total">
            <h4 className="text-right border-t pt-3.5">
              <CurrencyValue {...estimate.totalPayable} />
            </h4>
          </dd>
        </dl>
      </Accordion>
    </div>
  );
};

type GrantDeductionRowProps = {
  grant: NonNullable<
    ReturnType<typeof useGetProjectEstimate>['estimate']
  >['grants'][number];
};

const GrantDeductionRow: FC<GrantDeductionRowProps> = (props) => {
  const { grant } = props;
  const labelId = `estimate-grant-${grant.id}`;
  return (
    <>
      <dt id={labelId}>
        <p className="supporting-body pb-1.5">{grant.name}</p>
      </dt>
      <dd aria-labelledby={labelId}>
        <p className="text-right pb-1.5 text-green-600">
          −<CurrencyValue {...grant.amount} />
        </p>
      </dd>
    </>
  );
};

export default EstimateFinancials;
